import { Box, Group } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode } from 'react';
import SectionCard from './SectionCard';
import { SectionAddButton } from './SectionAddButton';

const ACCORDION_CHEVRON = <IconChevronDown size={20} stroke={1.5} />;

type CashflowSectionProps = {
  value: string;
  title: string;
  count: number;
  totalNode: ReactNode;
  showCount?: boolean;
  onAdd?: () => void;
  children: ReactNode;
};

export default function CashflowSection({
  value,
  title,
  count,
  totalNode,
  showCount = true,
  onAdd,
  children,
}: CashflowSectionProps) {
  const testId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <SectionCard
      value={value}
      title={title}
      count={count}
      showCount={showCount}
      variant="cells"
      chevron={ACCORDION_CHEVRON}
      style={{ borderRadius: 24 }}
      styles={{
        // The tint belongs to the item, not the control, so it sits behind the expanded
        // rows too rather than stopping at the header.
        item: {
          border: 'none',
          borderRadius: 24,
          backgroundColor: 'var(--mantine-color-gray-0)',
          overflow: 'hidden',
        },
        control: { backgroundColor: 'transparent', padding: '20px 24px' },
        label: { paddingTop: 0, paddingBottom: 0 },
        // The 'cells' variant paints .content white, which would stop the item's tint at the
        // header instead of carrying it behind the expanded rows.
        content: { padding: '20px 24px', backgroundColor: 'transparent' },
      }}
      controlTestId={`${testId}-control`}
      countTestId={`${testId}-count`}
      panelTestId={`${testId}-panel`}
      rightSection={
        <Group
          gap={24}
          wrap="nowrap"
          align="center"
          // Clears the chevron, which the accordion draws inside the control's right padding.
          mr={52}
          py="sm"
          style={{ position: 'relative', pointerEvents: 'none' }}
        >
          <Box data-testid={`${testId}-total`}>{totalNode}</Box>
          {onAdd && (
            <Box style={{ pointerEvents: 'auto' }}>
              <SectionAddButton
                action={{ onAdd }}
                ariaLabel={`Add ${title}`}
                testId={`${testId}-add`}
              />
            </Box>
          )}
        </Group>
      }
    >
      {children}
    </SectionCard>
  );
}
